// ─────────────────────────────────────────────────────────────────────────────
// builderTab.ts (Training)
//
// Lets the operator add new timers from the phone, mirroring the desktop
// builder cards:
//   • Single timer (title + minutes + seconds).
//   • Interval builder (rounds × work + rest).
//
// Both submit payloads the desktop side can fan out into one or more
// individual `TrainingAddTimer` operations.
// ─────────────────────────────────────────────────────────────────────────────

import { h } from "../../../ui/dom.js";
import { fireTraining, EventTypes } from "../fire.js";

export function builderTab(sessionId: string): HTMLElement {
	return h(
		"div",
		{ class: "space-y-3" },
		singleCard(sessionId),
		intervalCard(sessionId)
	);
}

function singleCard(sessionId: string): HTMLElement {
	const title = h("input", {
		type: "text",
		class: "field-input",
		placeholder: "Title (e.g. Warm-up)",
		maxlength: 64,
	}) as HTMLInputElement;
	const minutes = numberInput("0", 0, 99);
	const seconds = numberInput("30", 0, 59);

	const submit = (): void => {
		const t = title.value.trim() || "Timer";
		const m = clampInt(minutes.value, 0, 99, 0);
		const s = clampInt(seconds.value, 0, 59, 0);
		const total = m * 60 + s;
		if (total <= 0) return;
		void fireTraining(sessionId, EventTypes.TrainingAddTimer, `Added ${t}`, {
			title: t,
			seconds: total,
		}).then(() => {
			title.value = "";
		});
	};

	title.addEventListener("keydown", (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			submit();
		}
	});

	return h(
		"section",
		{ class: "card space-y-3" },
		h("div", { class: "field-label" }, "Add a single timer"),
		h(
			"label",
			{ class: "block" },
			h("span", { class: "field-label" }, "Title"),
			title
		),
		h(
			"div",
			{ class: "grid grid-cols-2 gap-2" },
			h(
				"label",
				{ class: "block" },
				h("span", { class: "field-label" }, "Minutes"),
				minutes
			),
			h(
				"label",
				{ class: "block" },
				h("span", { class: "field-label" }, "Seconds"),
				seconds
			)
		),
		h(
			"button",
			{ type: "button", class: "btn-primary w-full", onclick: submit },
			"Add timer"
		)
	);
}

function intervalCard(sessionId: string): HTMLElement {
	const rounds = numberInput("8", 1, 99);
	const workSec = numberInput("30", 5, 600);
	const restSec = numberInput("15", 0, 600);
	const titlePrefix = h("input", {
		type: "text",
		class: "field-input",
		placeholder: "Set 1",
		maxlength: 32,
	}) as HTMLInputElement;

	const build = (): void => {
		const r = clampInt(rounds.value, 1, 99, 1);
		const w = clampInt(workSec.value, 5, 600, 30);
		const rest = clampInt(restSec.value, 0, 600, 0);
		const prefix = titlePrefix.value.trim() || "Set";
		void fireTraining(sessionId, EventTypes.TrainingAddTimer, `Built ${r}×${w}/${rest}`, {
			interval: { rounds: r, workSeconds: w, restSeconds: rest, titlePrefix: prefix },
		});
	};

	return h(
		"section",
		{ class: "card space-y-3" },
		h("div", { class: "field-label" }, "Interval builder"),
		h(
			"label",
			{ class: "block" },
			h("span", { class: "field-label" }, "Title prefix"),
			titlePrefix
		),
		h(
			"div",
			{ class: "grid grid-cols-3 gap-2" },
			h(
				"label",
				{ class: "block" },
				h("span", { class: "field-label" }, "Rounds"),
				rounds
			),
			h(
				"label",
				{ class: "block" },
				h("span", { class: "field-label" }, "Work s"),
				workSec
			),
			h(
				"label",
				{ class: "block" },
				h("span", { class: "field-label" }, "Rest s"),
				restSec
			)
		),
		h(
			"button",
			{ type: "button", class: "btn-primary w-full", onclick: build },
			"Build interval set"
		)
	);
}

function numberInput(initial: string, min: number, max: number): HTMLInputElement {
	return h("input", {
		type: "number",
		min: String(min),
		max: String(max),
		inputmode: "numeric",
		class: "field-input text-center font-mono text-xl",
		value: initial,
	}) as HTMLInputElement;
}

function clampInt(value: string, min: number, max: number, fallback: number): number {
	const n = Math.trunc(Number(value));
	if (!Number.isFinite(n)) return fallback;
	return Math.max(min, Math.min(max, n));
}
